// Final-result computation for both game modes. The Classic function
// is the former inline computeFinalResult from reducer.js, moved
// verbatim; its output is locked by the reducer and final-score
// tests. The Advanced function mirrors it with the two-layer
// shortfall model and storage exclusions from
// design/canonical-numbers.md section 16.

import {
  lookupCarbonBracket,
  pickCarbonMultiplier
} from '../engine/carbonTax.js';
import { computeCleanEnergyAward, computeResilienceAward } from '../engine/awards.js';
import { PLANT_BY_ID } from '../data/plants.js';
import {
  ADVANCED_UNITS,
  ADVANCED_CARBON_YEAR_WEIGHTS
} from '../data/advancedCapacity.js';
import {
  GAME_MODE,
  CLASSIC_GAME_VERSION,
  ADVANCED_CAPACITY_GAME_VERSION
} from '../data/modes.js';
import { CARBON_TAX_BRACKETS } from '../data/brackets.js';

function mergeFamilyOutput(acc, familyOutput) {
  for (const [family, output] of Object.entries(familyOutput ?? {})) {
    acc[family] = (acc[family] ?? 0) + output;
  }
  return acc;
}

export function computeClassicFinalResult(state) {
  const totals = state.yearResults.reduce(
    (acc, y) => ({
      capital: acc.capital + y.capital,
      opex: acc.opex + y.opex,
      shortfall: acc.shortfall + y.shortfall,
      shortfallCost: acc.shortfallCost + y.shortfallCost,
      emissions: acc.emissions + y.emissions,
      totalOutput: acc.totalOutput + y.totalOutput,
      cleanEnergyOutput: acc.cleanEnergyOutput + y.cleanEnergyOutput,
      variableCleanEnergyOutput:
        acc.variableCleanEnergyOutput + (y.variableCleanEnergyOutput ?? 0),
      familyOutput: mergeFamilyOutput({ ...acc.familyOutput }, y.familyOutput)
    }),
    {
      capital: 0,
      opex: 0,
      shortfall: 0,
      shortfallCost: 0,
      emissions: 0,
      totalOutput: 0,
      cleanEnergyOutput: 0,
      variableCleanEnergyOutput: 0,
      familyOutput: {}
    }
  );
  const bracket = lookupCarbonBracket(totals.emissions, CARBON_TAX_BRACKETS);
  const carbonMultiplier = pickCarbonMultiplier(bracket, state.cop30);
  const carbonAmount = bracket.base * carbonMultiplier;
  const cleanAward = computeCleanEnergyAward({
    eligibleCleanMWh: totals.cleanEnergyOutput,
    variableCleanMWh: totals.variableCleanEnergyOutput,
    totalPostDisasterMWh: totals.totalOutput,
    portfolio: state.portfolio,
    plantsById: PLANT_BY_ID
  });
  const resilienceAwardResult = computeResilienceAward({
    portfolio: state.portfolio,
    familyMWh: totals.familyOutput,
    totalPostDisasterMWh: totals.totalOutput,
    plantsById: PLANT_BY_ID
  });
  const totalAwards = cleanAward.amount + resilienceAwardResult.amount;
  const totalCost =
    totals.capital +
    totals.opex +
    totals.shortfallCost -
    carbonAmount -
    totalAwards;
  return {
    ...totals,
    mode: GAME_MODE.CLASSIC,
    gameVersion: CLASSIC_GAME_VERSION,
    bracket,
    carbonMultiplier,
    carbonAmount,
    cleanEnergyShare: cleanAward.cleanShare,
    variableCleanEnergyShare: cleanAward.variableCleanShare,
    cleanEnergyAward: cleanAward.amount,
    cleanEnergyAwardTier: cleanAward.tier,
    cleanEnergyAwardEligible: cleanAward.eligible,
    cleanEnergyAwardFamilies: cleanAward.distinctFamiliesBuilt,
    resilienceAward: resilienceAwardResult.amount,
    resilienceAwardTier: resilienceAwardResult.tier,
    resilienceFamilies: resilienceAwardResult.distinctFamiliesBuilt,
    resilienceLargestFamilyShare: resilienceAwardResult.largestFamilyShare,
    totalAwards,
    totalCost
  };
}

// Advanced year results carry both the advanced field names and
// classic-named aliases (demand/totalOutput/shortfall/shortfallCost),
// so shared UI keeps working. This aggregation reads the advanced
// names and re-exposes the same aliases on the final result.
export function computeAdvancedFinalResult(state) {
  const totals = state.yearResults.reduce(
    (acc, y) => ({
      capital: acc.capital + y.capital,
      opex: acc.opex + y.opex,
      decommissionCost: acc.decommissionCost + (y.decommissionCost ?? 0),
      resourceDevelopmentPremium:
        acc.resourceDevelopmentPremium +
        (y.resourceDevelopmentPremium ?? 0),
      energyShortfall: acc.energyShortfall + y.energyShortfall,
      energyShortfallCost: acc.energyShortfallCost + y.energyShortfallCost,
      capacityShortfall: acc.capacityShortfall + y.capacityShortfall,
      capacityShortfallCost:
        acc.capacityShortfallCost + y.capacityShortfallCost,
      emissions: acc.emissions + y.emissions,
      // Escalating carbon price (section 16.10): the bracket lookup
      // uses year-weighted emissions; raw emissions stay for display.
      weightedEmissions:
        acc.weightedEmissions +
        (y.carbonEmissions ?? y.emissions) *
          (ADVANCED_CARBON_YEAR_WEIGHTS[y.year] ?? 1),
      totalOutput: acc.totalOutput + y.actualAnnualGeneration,
      cleanEnergyOutput: acc.cleanEnergyOutput + y.cleanEnergyOutput,
      variableCleanEnergyOutput:
        acc.variableCleanEnergyOutput + (y.variableCleanEnergyOutput ?? 0),
      familyOutput: mergeFamilyOutput({ ...acc.familyOutput }, y.familyOutput)
    }),
    {
      capital: 0,
      opex: 0,
      decommissionCost: 0,
      resourceDevelopmentPremium: 0,
      energyShortfall: 0,
      energyShortfallCost: 0,
      capacityShortfall: 0,
      capacityShortfallCost: 0,
      emissions: 0,
      weightedEmissions: 0,
      totalOutput: 0,
      cleanEnergyOutput: 0,
      variableCleanEnergyOutput: 0,
      familyOutput: {}
    }
  );
  // Weighted emissions are rounded to a whole unit so the
  // integer-banded Classic bracket table applies (section 16.10).
  const bracket = lookupCarbonBracket(
    Math.round(totals.weightedEmissions),
    CARBON_TAX_BRACKETS
  );
  const carbonMultiplier = pickCarbonMultiplier(bracket, state.cop30);
  const carbonAmount = bracket.base * carbonMultiplier;
  const cleanEnergyAward = 0;
  const resilienceAward = 0;
  const affordabilityAward = 0;
  const totalAwards = 0;
  const energyDelivered = state.yearResults.reduce(
    (sum, year) =>
      sum + Math.max(0, year.annualEnergyDemand - year.energyShortfall),
    0
  );
  const costPerEnergyDelivered =
    energyDelivered > 0
      ? (totals.capital + totals.resourceDevelopmentPremium + totals.opex) /
        energyDelivered
      : null;
  const cleanEnergyShare =
    totals.totalOutput > 0
      ? totals.cleanEnergyOutput / totals.totalOutput
      : 0;
  const variableCleanEnergyShare =
    totals.totalOutput > 0
      ? totals.variableCleanEnergyOutput / totals.totalOutput
      : 0;
  const totalCost =
    totals.capital +
    totals.resourceDevelopmentPremium +
    totals.opex +
    totals.decommissionCost +
    totals.energyShortfallCost +
    totals.capacityShortfallCost -
    carbonAmount -
    totalAwards;
  return {
    ...totals,
    weightedEmissions: Math.round(totals.weightedEmissions),
    mode: GAME_MODE.ADVANCED_CAPACITY,
    gameVersion: ADVANCED_CAPACITY_GAME_VERSION,
    units: ADVANCED_UNITS,
    shortfall: totals.energyShortfall,
    shortfallCost: totals.energyShortfallCost,
    bracket,
    carbonMultiplier,
    carbonAmount,
    cleanEnergyShare,
    variableCleanEnergyShare,
    cleanEnergyAward,
    cleanEnergyAwardTier: null,
    cleanEnergyAwardEligible: false,
    cleanEnergyAwardFamilies: 0,
    resilienceAward,
    resilienceAwardTier: null,
    resilienceFamilies: 0,
    resilienceLargestFamilyShare: 0,
    affordabilityAward,
    affordabilityAwardTier: null,
    affordabilityAwardEligible: false,
    energyDelivered,
    costPerEnergyDelivered,
    awardBreakdown: [],
    totalAwards,
    totalCost
  };
}
